import axios from '../config/axios';

export interface ApiKey {
  id: number;
  name: string;
  key_prefix: string;
  key?: string; // Full key only returned during creation
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
}

export interface TimeSeriesPoint {
  timestamp: string;
  total: number;
  failed: number;
}

export interface ApiStats {
  total_requests: number;
  requests_this_week: number;
  failed_requests: number;
  success_rate: number;
  top_endpoints: {
    endpoint: string;
    count: number;
  }[];
  time_series: TimeSeriesPoint[];
}

export interface CreateApiKeyRequest {
  name: string;
}

export interface DeveloperAccountStats {
  user_id: number;
  email: string;
  total_recordings: number;
  processed_recordings: number;
  failed_recordings: number;
  total_duration_minutes: number;
  account_type: string;
}

export interface Recording {
  id: string;
  title: string;
  status: string;
  duration: number;
  created_at: string;
  media_type: string;
  filename?: string;
  file_size?: number;
  patient_name?: string;
  visit_type?: string;
  preview?: string;
}

export interface RecordingListParams {
  limit?: number;
  offset?: number;
  status?: string;
}

export interface ApiLog {
  id: string;
  method: string;
  endpoint: string;
  status: number;
  created: string;
  ip_address: string;
  response_time_ms: number;
  api_key_used: string;
  user_agent?: string;
  origin?: string | null;
  query_params?: Record<string, any>;
  request_headers?: Record<string, string>;
  request_body?: any;
  response_body?: any;
  request_size_bytes?: number;
  response_size_bytes?: number;
  success?: boolean;
  request_type?: string;
  model_used?: string;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  pricing?: {
    prompt_cost_usd: number;
    completion_cost_usd: number;
    total_cost_usd: number;
    cost_per_token: number;
  };
  error_message?: string | null;
}

export interface ApiLogListParams {
  limit?: number;
  offset?: number;
}

export const developerApiService = {
  // API Key Management
  async createApiKey(request: CreateApiKeyRequest): Promise<ApiKey> {
    try {
      console.log('Creating API key with name:', request.name);
      const response = await axios.post('/api/scribing/developer/api-keys', request);
      console.log('API key created successfully');
      return response.data;
    } catch (error: any) {
      console.error('Error creating API key:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create API key';
      throw new Error(errorMessage);
    }
  },

  // API Usage Stats
  async getApiStats(timeframe?: string): Promise<ApiStats> {
    try {
      console.log('Fetching API usage statistics', { timeframe });
      const query = timeframe ? `?timeframe=${encodeURIComponent(timeframe)}` : '';
      const response = await axios.get(`/api/scribing/developer/api-stats${query}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching API stats:', error);
      throw error;
    }
  },

  async listApiKeys(): Promise<ApiKey[]> {
    try {
      console.log('Fetching API keys list');
      const response = await axios.get('/api/scribing/developer/api-keys');
      console.log('API keys fetched successfully, count:', response.data.length);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching API keys:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch API keys';
      throw new Error(errorMessage);
    }
  },

  async deleteApiKey(keyId: number): Promise<void> {
    try {
      console.log('Deactivating API key:', keyId);
      await axios.delete(`/api/scribing/developer/api-keys/${keyId}`);
      console.log('API key deactivated successfully');
    } catch (error: any) {
      console.error('Error deactivating API key:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to deactivate API key';
      throw new Error(errorMessage);
    }
  },

  // Developer API Endpoints (using API keys)
  async testApiKey(apiKey: string): Promise<{ status: string; user_id: number; message: string }> {
    try {
      console.log('Testing API key authentication');
      const response = await axios.get('/api/scribing/developer-api/v1/health', {
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      });
      console.log('API key test successful');
      return response.data;
    } catch (error: any) {
      console.error('Error testing API key:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'API key test failed';
      throw new Error(errorMessage);
    }
  },

  async getAccountStats(apiKey: string): Promise<DeveloperAccountStats> {
    try {
      console.log('Fetching account statistics');
      const response = await axios.get('/api/scribing/developer-api/v1/account/stats', {
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      });
      console.log('Account stats fetched successfully');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching account stats:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch account statistics';
      throw new Error(errorMessage);
    }
  },

  async getRecordings(apiKey: string, params: RecordingListParams = {}): Promise<Recording[]> {
    try {
      console.log('Fetching recordings list');
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());
      if (params.status) queryParams.append('status', params.status);

      const url = `/api/scribing/developer-api/v1/recordings${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      });
      console.log('Recordings fetched successfully, count:', response.data.length);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching recordings:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch recordings';
      throw new Error(errorMessage);
    }
  },

  async getRecording(apiKey: string, recordingId: string): Promise<Recording> {
    try {
      console.log('Fetching recording details:', recordingId);
      const response = await axios.get(`/api/scribing/developer-api/v1/recordings/${recordingId}`, {
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      });
      console.log('Recording details fetched successfully');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching recording details:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch recording details';
      throw new Error(errorMessage);
    }
  },

  // API Logs Management
  async getApiLogs(params: ApiLogListParams = {}, apiKey?: string): Promise<ApiLog[]> {
    try {
      console.log('Fetching API logs');
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());

      const url = `/api/scribing/developer/api-logs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axios.get(url, {
        headers: apiKey ? { Authorization: `Bearer ${apiKey}` } : undefined
      });
      console.log('API logs fetched successfully, count:', response.data.length);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching API logs:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch API logs';
      throw new Error(errorMessage);
    }
  },

  async getApiLog(logId: string, apiKey?: string): Promise<ApiLog> {
    try {
      console.log('Fetching API log details:', logId);
      const response = await axios.get(`/api/scribing/developer/api-logs/${logId}`, {
        headers: apiKey ? { Authorization: `Bearer ${apiKey}` } : undefined
      });
      console.log('API log details fetched successfully');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching API log details:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch API log details';
      throw new Error(errorMessage);
    }
  }
};